import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'

import {
  FORM_FIELDS,
  type FormData,
} from '@/pages/getRecipe/fullLayering/framework/utils/formTypes'

import { PLACEMENT } from './appSlice'
import type { CrownInput, StumpShadeInput } from './stainingStudio'

interface StumpShadeStainingRecipe {
  [PLACEMENT.LEFT]: { [key: string]: { input: CrownInput; output: any } }
  [PLACEMENT.RIGHT]: { [key: string]: { input: CrownInput; output: any } }
}

interface stainingCorrectionRecipe {
  [PLACEMENT.LEFT]: { [key: string]: { input: CrownInput; output: any } }
  [PLACEMENT.RIGHT]: { [key: string]: { input: CrownInput; output: any } }
}
export interface fullLayeringState {
  frameworkRecipeActiveStep: number
  frameWorkform: { [key in keyof FormData]?: any }
  selectedPreparationKey: string
  selectedTargetKey: string
  preparationTargetColor: any
  targetColor: any
  frameWorkRecipeOutput: any
  stumpShadeTargetColor: any
  stumpShadePreparationColor: any
  stumpSahdeStainingBrand: string
  stumpShadeRecipeOutput: any
  recipeType: string
  selectedStumpShadeBrand: string | null
  selectedStumpShadeKey: string | null
  stumpShadeRecipe: StumpShadeStainingRecipe | null
  washBake: any
  selectedWashbakeKey: string
  washbakeStainingBrand: string
  selectedWashbakeFrameworkKey: string
  frameworkTargetColor: any
  washBakeRecipeOutput: any
  selectedDentinColorKey: string
  dentinColor: {
    [imageId: string]: {
      [key: string]: {
        targetcolor?: (number | null | string)[]
        position?: { x: number; y: number }
        output?: any
      }
    }
  }
  dentinCeramicBrand: string
  dentinRecipeOutput: any
  internalEffectCeramicBrand: string
  selectedInternalEffect: string
  internalEffects: any
  internalEffectOutput: any
  selectedStainingCorrectionKey: string | null
  stainingCorrectionRecipe: stainingCorrectionRecipe | null
  selectedStainingCorrectionBrand: string | null
  enamelCeramicBrand: string
  enamelTypesByRegion: Record<string, string>
  measuringRegion: string
  enamelRecipe: any
  enamelPosition: any
  frameworkRecipeType: string
  DentinColorState: any
  availableSpace: string | null
}

const initialState: fullLayeringState = {
  frameworkRecipeActiveStep: 0,
  frameWorkform: {
    [FORM_FIELDS.availspace_CERVICAL]: 0.5,
    [FORM_FIELDS.availspace_INCISAL]: 0.5,
    [FORM_FIELDS.availspace_MIDDLE]: 0.5,
    [FORM_FIELDS.INDICATION]: '',
    [FORM_FIELDS.CAPBRAND]: '',
  },
  selectedPreparationKey: '',
  selectedTargetKey: '',
  preparationTargetColor: {},
  targetColor: {},
  frameWorkRecipeOutput: {},
  frameworkRecipeType: 'Single Zone',

  //Stump Shade
  stumpSahdeStainingBrand: '',
  stumpShadeTargetColor: {},
  stumpShadePreparationColor: {},
  stumpShadeRecipeOutput: {},
  recipeType: 'Single Zone',

  //Stump Shade Staining
  selectedStumpShadeBrand: null,
  selectedStumpShadeKey: null,
  stumpShadeRecipe: { [PLACEMENT.LEFT]: {}, [PLACEMENT.RIGHT]: {} },

  //Wash bake
  washbakeStainingBrand: '',
  washBake: { [PLACEMENT.LEFT]: {}, [PLACEMENT.RIGHT]: {} },
  selectedWashbakeKey: '',
  selectedWashbakeFrameworkKey: '',
  frameworkTargetColor: {},
  washBakeRecipeOutput: {},
  availableSpace: null,

  //Dentin
  dentinColor: {},
  selectedDentinColorKey: '',
  dentinCeramicBrand: '',
  dentinRecipeOutput: {},
  DentinColorState: {},

  //Internal Effect
  internalEffectCeramicBrand: '',
  selectedInternalEffect: '',
  internalEffects: {},
  internalEffectOutput: {},

  //Staining correction
  selectedStainingCorrectionKey: null,
  stainingCorrectionRecipe: { [PLACEMENT.LEFT]: {}, [PLACEMENT.RIGHT]: {} },
  selectedStainingCorrectionBrand: null,

  //Enamel
  enamelCeramicBrand: '',
  enamelTypesByRegion: {},
  measuringRegion: '',
  enamelRecipe: {},
  enamelPosition: {},
}

export const fullLayeringSlice = createSlice({
  name: 'fullLayering',
  initialState,
  reducers: {
    setFrameWorkRecipeActiveStep: (state, action: PayloadAction<number>) => {
      return { ...state, frameworkRecipeActiveStep: action.payload }
    },
    updateFrameWorkFormValue: (
      state,
      action: PayloadAction<{ name: keyof FormData; value: any }>,
    ) => {
      return {
        ...state,
        frameWorkform: {
          ...state.frameWorkform,
          [action.payload.name]: action.payload.value,
        },
      }
    },
    setSelectedPreparationKey: (state, action: PayloadAction<string>) => {
      state.selectedPreparationKey = action.payload
    },
    setSelectedTargetKey: (state, action: PayloadAction<string>) => {
      state.selectedTargetKey = action.payload
    },
    setPreparationTarget: (
      state,
      action: PayloadAction<{
        targetcolor: (number | null | string)[]
        position: 'cervical' | 'middle' | 'incisal'
        imagePosition: { x: number; y: number }
      }>,
    ) => {
      const { targetcolor, position, imagePosition } = action.payload

      state.preparationTargetColor[position] = {
        targetcolor,
        imagePosition,
      }
    },
    setTargetColor: (
      state,
      action: PayloadAction<{
        targetcolor: (number | null | string)[]
        position: 'cervical' | 'middle' | 'incisal'
        imagePosition: { x: number; y: number }
      }>,
    ) => {
      const { targetcolor, position, imagePosition } = action.payload

      state.targetColor[position] = { targetcolor, imagePosition }
    },
    setFrameWorkRecipeOutput: (state, action) => {
      const { imageId, result, generation_id } = action.payload

      if (!state.frameWorkRecipeOutput) {
        state.frameWorkRecipeOutput = {}
      }
      if (result === undefined) {
        delete state.frameWorkRecipeOutput[imageId]
      } else {
        state.frameWorkRecipeOutput[imageId] = {
          ...result,
          generation_id,
          generatedAt: Date.now(),
          expiresAt: Date.now() + 24 * 60 * 60 * 1000,
        }
      }
    },

    resetFrameworkRecipeState: (
      state,
      action: PayloadAction<{ imageId: string }>,
    ) => {
      const { imageId } = action.payload

      delete state.frameWorkRecipeOutput[imageId]
      state.preparationTargetColor = {}
      state.targetColor = {}
      state.frameWorkform = initialState.frameWorkform
      state.frameworkRecipeActiveStep = initialState.frameworkRecipeActiveStep

      state.selectedPreparationKey = initialState.selectedPreparationKey
      state.selectedTargetKey = initialState.selectedTargetKey
    },
    setFrameworkRecipeType: (state, action) => {
      state.frameworkRecipeType = action.payload
    },

    //Stump Shade
    setStumpShadeStainingBrand: (state, action: PayloadAction<string>) => {
      state.stumpSahdeStainingBrand = action.payload
    },
    setStumpShadePreparationTarget: (
      state,
      action: PayloadAction<{
        imageId: string
        targetcolor: (number | null | string)[]
        position: 'cervical' | 'middle' | 'incisal'
        imagePosition: { x: number; y: number }
      }>,
    ) => {
      const { imageId, targetcolor, position, imagePosition } = action.payload

      if (!state.stumpShadePreparationColor[imageId]) {
        state.stumpShadePreparationColor[imageId] = {}
      }

      state.stumpShadePreparationColor[imageId][position] = {
        targetcolor,
        imagePosition,
      }
    },
    setStumpShadeTargetColor: (
      state,
      action: PayloadAction<{
        imageId: string
        targetcolor: (number | null | string)[]
        position: 'cervical' | 'middle' | 'incisal'
        imagePosition: { x: number; y: number }
      }>,
    ) => {
      const { imageId, targetcolor, position, imagePosition } = action.payload

      if (!state.stumpShadeTargetColor[imageId]) {
        state.stumpShadeTargetColor[imageId] = {}
      }

      state.stumpShadeTargetColor[imageId][position] = {
        targetcolor,
        imagePosition,
      }
    },
    setStumpShadeRecipeOutput: (state, action) => {
      const { imageId, key, result, generation_id } = action.payload

      if (!state.stumpShadeRecipeOutput) {
        state.stumpShadeRecipeOutput = {}
      }

      if (!state.stumpShadeRecipeOutput[imageId]) {
        state.stumpShadeRecipeOutput[imageId] = {}
      }

      if (result === undefined) {
        delete state.stumpShadeRecipeOutput[imageId][key]
      } else {
        state.stumpShadeRecipeOutput[imageId][key] = {
          ...result,
          generation_id,
          generatedAt: Date.now(),
          expiresAt: Date.now() + 24 * 60 * 60 * 1000,
        }
      }
    },
    removeStumpShadeTargetColor: (state, action) => {
      const { imageId, key } = action.payload
      delete state.stumpShadeTargetColor?.[imageId]?.[key]
    },
    removeStumpShadePreparationColor: (state, action) => {
      const { imageId, key } = action.payload
      delete state.stumpShadePreparationColor?.[imageId]?.[key]
    },
    setRecipeType: (state, action) => {
      state.recipeType = action.payload
    },

    //Stump Sahde Staining
    setSelectedStumpShadeStainingBrand: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      return { ...state, selectedStumpShadeBrand: action.payload }
    },
    setSelectedStumpShadeKey: (state, action: PayloadAction<string | null>) => {
      return { ...state, selectedStumpShadeKey: action.payload }
    },
    setStumpShadeRecipe: (
      state,
      action: PayloadAction<{
        placement: PLACEMENT
        key: string
        data: {
          input?: StumpShadeInput
          output?: any
          generation_id?: string | number
        }
      }>,
    ) => {
      const placement = action.payload
        .placement as unknown as keyof StumpShadeStainingRecipe
      const currentRecipe = state.stumpShadeRecipe || {
        [PLACEMENT.LEFT]: {},
        [PLACEMENT.RIGHT]: {},
      }

      const expiresAt = Date.now() + 24 * 60 * 60 * 1000

      return {
        ...state,
        stumpShadeRecipe: {
          ...currentRecipe,
          [placement]: {
            ...currentRecipe[placement],
            [action.payload.key]: {
              ...currentRecipe[placement][action.payload.key],
              ...action.payload.data,
              expiresAt,
            },
          },
        },
      }
    },
    removeStumpShadeRecipeKey: (
      state,
      action: PayloadAction<{ key: string }>,
    ) => {
      if (state?.stumpShadeRecipe?.[PLACEMENT.LEFT]) {
        delete state.stumpShadeRecipe[PLACEMENT.LEFT][action.payload.key]
      }
      if (state?.stumpShadeRecipe?.[PLACEMENT.RIGHT]) {
        delete state.stumpShadeRecipe[PLACEMENT.RIGHT][action.payload.key]
      }
    },
    clearStumpShadeStainingState: () => initialState,

    //WASHBAKE
    setWashBakeStainingBrand: (state, action: PayloadAction<string>) => {
      state.washbakeStainingBrand = action.payload
    },
    setSelectedWashbakeKey: (state, action: PayloadAction<string>) => {
      state.selectedWashbakeKey = action.payload
    },
    setWashbakeTarget: (
      state,
      action: PayloadAction<{
        targetcolor?: (number | null | string)[]
        position?: { x: number; y: number }
        output?: any
      }>,
    ) => {
      const { targetcolor, position, output } = action.payload
      const key = state.selectedWashbakeKey
      if (!key) return

      if (!state.washBake) {
        state.washBake = {}
      }

      const existing = state.washBake[key] || {}

      state.washBake[key] = {
        ...existing,
        ...(targetcolor && { targetcolor }),
        ...(position && { position }),
        ...(output && { output: { ...existing.output, ...output } }),
      }
    },
    setSelectedWashbakeFrameworkKey: (state, action: PayloadAction<string>) => {
      state.selectedWashbakeFrameworkKey = action.payload
    },
    setWashBakeFrameWorkTarget: (
      state,
      action: PayloadAction<{
        targetcolor: (number | null | string)[]
        position: 'cervical' | 'middle' | 'incisal'
        imagePosition: { x: number; y: number }
      }>,
    ) => {
      const { targetcolor, position, imagePosition } = action.payload
      state.frameworkTargetColor[position] = { targetcolor, imagePosition }
    },
    setWashBakeRecipeOutput: (
      state,
      action: PayloadAction<{
        placement: PLACEMENT
        key: string
        data: { input?: any; output?: any; generation_id?: string | number }
      }>,
    ) => {
      const { placement, key, data } = action.payload

      const currentRecipe = state.washBake || {
        [PLACEMENT.LEFT]: {},
        [PLACEMENT.RIGHT]: {},
      }

      return {
        ...state,
        washBake: {
          ...currentRecipe,
          [placement]: {
            ...currentRecipe[placement],
            [key]: {
              ...(currentRecipe[placement]?.[key] || {}),
              ...data,
            },
          },
        },
      }
    },
    removeWashBakeTarget: (state, action: PayloadAction<{ key: string }>) => {
      if (state?.washBake?.[PLACEMENT.LEFT]) {
        delete state.washBake[PLACEMENT.LEFT][action.payload.key]
      }
      if (state?.washBake?.[PLACEMENT.RIGHT]) {
        delete state.washBake[PLACEMENT.RIGHT][action.payload.key]
      }
    },

    setAvailableSpace(state, action: PayloadAction<string>) {
      state.availableSpace = action.payload
    },

    //Dentin
    setSelectedDentinColorKey: (state, action: PayloadAction<string>) => {
      state.selectedDentinColorKey = action.payload
    },
    setDentinColorTarget: (
      state,
      action: PayloadAction<{
        imageId: string
        targetcolor?: (number | null | string)[]
        position?: { x: number; y: number }
        output?: any
        key?: string
      }>,
    ) => {
      const {
        imageId,
        key: actionKey,
        targetcolor,
        position,
        output,
      } = action.payload
      const key = actionKey || state.selectedDentinColorKey
      if (!key || !imageId) return

      if (!state.dentinColor) {
        state.dentinColor = {}
      }

      if (!state.dentinColor[imageId]) {
        state.dentinColor[imageId] = {}
      }

      if (!state.dentinColor[imageId][key]) {
        state.dentinColor[imageId][key] = {}
      }

      const existing = state.dentinColor[imageId][key]

      state.dentinColor[imageId][key] = {
        ...existing,
        ...(targetcolor && { targetcolor }),
        ...(position && { position }),
        ...(output && { output: { ...existing.output, ...output } }),
      }
    },
    setDentinCeramicBrand: (state, action: PayloadAction<string>) => {
      state.dentinCeramicBrand = action.payload
    },
    setdentinRecipeOutput: (state, action) => {
      const { imageId, key, result, generation_id } = action.payload
      if (!state.dentinRecipeOutput) {
        state.dentinRecipeOutput = {}
      }

      if (!state.dentinRecipeOutput[imageId]) {
        state.dentinRecipeOutput[imageId] = {}
      }

      if (result === undefined) {
        delete state.dentinRecipeOutput[imageId][key]
      } else {
        state.dentinRecipeOutput[imageId][key] = {
          ...result,
          generation_id,
          generatedAt: Date.now(),
          expiresAt: Date.now() + 24 * 60 * 60 * 1000,
        }
      }
    },
    removeDentinTarget: (
      state,
      action: PayloadAction<{ imageId: string; key: string }>,
    ) => {
      const { imageId, key } = action.payload

      if (state.dentinColor?.[imageId]?.[key]) {
        delete state.dentinColor[imageId][key]

        if (Object.keys(state.dentinColor[imageId]).length === 0) {
          delete state.dentinColor[imageId]
        }
      }

      if (state.DentinColorState?.[imageId]?.[key]) {
        delete state.DentinColorState[imageId][key]
        if (Object.keys(state.DentinColorState[imageId]).length === 0) {
          delete state.DentinColorState[imageId]
        }
      }
    },

    //Internal Effect
    setInternalEffectCeramicBrand: (state, action: PayloadAction<string>) => {
      state.internalEffectCeramicBrand = action.payload
    },
    setSelectedInternalEffect: (state, action: PayloadAction<string>) => {
      state.selectedInternalEffect = action.payload
    },
    setInternalEffectPosition: (
      state,
      action: PayloadAction<{
        imageId: string
        targetcolor?: (number | null | string)[]
        position?: { x: number; y: number }
        output?: any
      }>,
    ) => {
      const { imageId, position, output } = action.payload
      const key = state.selectedInternalEffect
      if (!key || !imageId) return

      if (!state.internalEffects) {
        state.internalEffects = {}
      }

      if (!state.internalEffects[imageId]) {
        state.internalEffects[imageId] = {}
      }

      if (!state.internalEffects[imageId][key]) {
        state.internalEffects[imageId][key] = {}
      }

      const existing = state.internalEffects[imageId][key]

      state.internalEffects[imageId][key] = {
        ...existing,
        ...(position && { position }),
        ...(output && { output: { ...existing.output, ...output } }),
      }
    },
    setInternalEffectRecipeOutput: (state, action) => {
      const { imageId, key, result, generation_id } = action.payload

      if (!state.internalEffectOutput) {
        state.internalEffectOutput = {}
      }

      if (!state.internalEffectOutput[imageId]) {
        state.internalEffectOutput[imageId] = {}
      }

      if (result === undefined) {
        delete state.internalEffectOutput[imageId][key]
      } else {
        state.internalEffectOutput[imageId][key] = {
          ...result,
          generation_id,
          generatedAt: Date.now(),
          expiresAt: Date.now() + 24 * 60 * 60 * 1000,
        }
      }
    },

    //Staining correction
    setSelectedStainingCorrectionKey: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      return { ...state, selectedStainingCorrectionKey: action.payload }
    },
    setSelectedStainngCorrectionBrand: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      return { ...state, selectedStainingCorrectionBrand: action.payload }
    },
    setStainingCorrectionRecipe: (
      state,
      action: PayloadAction<{
        placement: PLACEMENT
        key: string
        data: {
          input?: CrownInput
          output?: any
          generation_id?: string | number
        }
      }>,
    ) => {
      const placement = action.payload
        .placement as keyof stainingCorrectionRecipe
      const currentRecipe = state.stainingCorrectionRecipe || {
        [PLACEMENT.LEFT]: {},
        [PLACEMENT.RIGHT]: {},
      }
      const expiresAt = Date.now() + 24 * 60 * 60 * 1000

      return {
        ...state,
        stainingCorrectionRecipe: {
          ...currentRecipe,
          [placement]: {
            ...currentRecipe[placement],
            [action.payload.key]: {
              ...currentRecipe[placement][action.payload.key],
              ...action.payload.data,
              expiresAt,
            },
          },
        },
      }
    },
    clearStainingCorrectionState: () => initialState,
    removeStainingCorrectionKey: (
      state,
      action: PayloadAction<{ key: string }>,
    ) => {
      if (state?.stainingCorrectionRecipe?.[PLACEMENT.LEFT]) {
        delete state.stainingCorrectionRecipe[PLACEMENT.LEFT][
          action.payload.key
        ]
      }
      if (state?.stainingCorrectionRecipe?.[PLACEMENT.RIGHT]) {
        delete state.stainingCorrectionRecipe[PLACEMENT.RIGHT][
          action.payload.key
        ]
      }
    },

    //Enamel
    setEnamelCeramicBrand: (state, action: PayloadAction<string>) => {
      state.enamelCeramicBrand = action.payload
    },
    setEnamelTypeByRegion(
      state,
      action: PayloadAction<{ region: string; value: string }>,
    ) {
      if (action.payload.value === '') {
        delete state.enamelTypesByRegion[action.payload.region]
      } else {
        state.enamelTypesByRegion[action.payload.region] = action.payload.value
      }
    },
    setMeasuringRegion: (state, action) => {
      state.measuringRegion = action.payload
    },
    setEnamelRecipeOutput: (state, action) => {
      const { imageId, key, result, generation_id } = action.payload

      if (!state.enamelRecipe) {
        state.enamelRecipe = {}
      }

      if (!state.enamelRecipe[imageId]) {
        state.enamelRecipe[imageId] = {}
      }

      if (result === undefined) {
        delete state.enamelRecipe[imageId][key]
      } else {
        state.enamelRecipe[imageId][key] = {
          ...result,
          generation_id,
          generatedAt: Date.now(),
          expiresAt: Date.now() + 24 * 60 * 60 * 1000,
        }
      }
    },
    setEnamelPosition: (
      state,
      action: PayloadAction<{
        imageId: string
        targetcolor?: (number | null | string)[]
        position?: { x: number; y: number }
        output?: any
      }>,
    ) => {
      const { imageId, position, output } = action.payload
      const key = state.measuringRegion
      if (!key || !imageId) return

      if (!state.enamelPosition) {
        state.enamelPosition = {}
      }

      if (!state.enamelPosition[imageId]) {
        state.enamelPosition[imageId] = {}
      }

      if (!state.enamelPosition[imageId][key]) {
        state.enamelPosition[imageId][key] = {}
      }

      const existing = state.enamelPosition[imageId][key]

      state.enamelPosition[imageId][key] = {
        ...existing,
        ...(position && { position }),
        ...(output && { output: { ...existing.output, ...output } }),
      }
    },

    setDentinTargetColor: (
      state,
      action: PayloadAction<{
        imageId: string
        key: string
        targetcolor: number[]
      }>,
    ) => {
      const { imageId, key, targetcolor } = action.payload
      if (!imageId || !key) return

      if (!state.DentinColorState[imageId]) {
        state.DentinColorState[imageId] = {}
      }

      state.DentinColorState[imageId][key] = targetcolor
    },

    clearFLWashBakeState: (state) => {
      state.washBake = initialState.washBake
      state.selectedWashbakeKey = initialState.selectedWashbakeKey
      state.selectedWashbakeFrameworkKey =
        initialState.selectedWashbakeFrameworkKey
      state.frameworkTargetColor = initialState.frameworkTargetColor
      state.washBakeRecipeOutput = initialState.washBakeRecipeOutput
    },
  },
})

export const {
  setFrameWorkRecipeActiveStep,
  updateFrameWorkFormValue,
  setSelectedPreparationKey,
  setSelectedTargetKey,
  setPreparationTarget,
  setTargetColor,
  setFrameWorkRecipeOutput,
  resetFrameworkRecipeState,
  setFrameworkRecipeType,

  //Stump shade
  setStumpShadePreparationTarget,
  setStumpShadeTargetColor,
  setStumpShadeStainingBrand,
  setStumpShadeRecipeOutput,
  removeStumpShadeTargetColor,
  removeStumpShadePreparationColor,
  setRecipeType,

  //Stump Shade Staining
  setSelectedStumpShadeStainingBrand,
  setSelectedStumpShadeKey,
  setStumpShadeRecipe,
  removeStumpShadeRecipeKey,
  clearStumpShadeStainingState,

  //WASHBAKE
  setWashBakeStainingBrand,
  setSelectedWashbakeKey,
  setWashbakeTarget,
  setSelectedWashbakeFrameworkKey,
  setWashBakeFrameWorkTarget,
  setWashBakeRecipeOutput,
  removeWashBakeTarget,
  setAvailableSpace,
  clearFLWashBakeState,

  //Dentin
  setSelectedDentinColorKey,
  setDentinColorTarget,
  setDentinCeramicBrand,
  setdentinRecipeOutput,
  removeDentinTarget,
  setDentinTargetColor,

  //Internal Effect
  setInternalEffectCeramicBrand,
  setSelectedInternalEffect,
  setInternalEffectPosition,
  setInternalEffectRecipeOutput,

  //Staining correction
  setSelectedStainingCorrectionKey,
  setSelectedStainngCorrectionBrand,
  setStainingCorrectionRecipe,
  clearStainingCorrectionState,
  removeStainingCorrectionKey,

  //Enamel
  setEnamelCeramicBrand,
  setEnamelTypeByRegion,
  setMeasuringRegion,
  setEnamelRecipeOutput,
  setEnamelPosition,
} = fullLayeringSlice.actions
export default fullLayeringSlice.reducer
